import { readAppOrigin } from '../api/config'

/**
 * 参考图外链：按存储链路区分展示 URL（应用内磁盘 vs 七牛/外链 CDN）。
 */

export type ReferenceImageLinkKind = 'app-hosted' | 'remote-cdn'

export type ReferenceImageUrlHints = {
  /** 后端 bucket；本地磁盘为 `local`，七牛为真实桶名 */
  bucket?: string
  objectKey?: string
}

const LOCAL_BUCKET = 'local'

function isLikelySignedPrivateUrl(u: URL): boolean {
  return u.searchParams.has('e') && u.searchParams.has('token')
}

/** 是否为七牛私有桶下载签名链（带 e/token） */
export function isSignedPrivateDownloadUrl(raw: string): boolean {
  const parsed = parseHttpUrl(raw.trim())
  return parsed != null && isLikelySignedPrivateUrl(parsed)
}

/** 签名链在 skew 秒内仍视为有效（用于避免 Referer 403 时无意义 refresh-url） */
export function signedPrivateDownloadUrlStillValid(raw: string, skewSeconds = 120): boolean {
  const parsed = parseHttpUrl(raw.trim())
  if (!parsed || !isLikelySignedPrivateUrl(parsed)) {
    return false
  }
  const e = parsed.searchParams.get('e')
  if (!e) return false
  const expSec = Number.parseInt(e, 10)
  if (!Number.isFinite(expSec)) return false
  return expSec * 1000 > Date.now() + skewSeconds * 1000
}

/** 判断是否为七牛CDN域名 */
export function isQiniuCdnHost(hostname: string): boolean {
  const h = hostname.toLowerCase()
  return (
    h.includes('qiniucdn.') ||
    h.includes('qiniucdn.com') ||
    h.includes('qiniudn.com') ||
    h.includes('clouddn.com') ||
    h.includes('qbox.me')
  )
}

/** 常见七牛公开访问域名后缀（自定义 CDN 域名无法枚举，仅作启发式优化） */
export function isLikelyQiniuHost(hostname: string): boolean {
  return isQiniuCdnHost(hostname)
}

function safeDecodePathSegment(segment: string): string {
  try {
    return decodeURIComponent(segment)
  } catch {
    return segment
  }
}

function readAppOriginSafe(): string {
  try {
    return readAppOrigin().replace(/\/+$/, '')
  } catch {
    return ''
  }
}

function readPageOriginSafe(): string {
  try {
    if (typeof window !== 'undefined' && window.location?.origin) {
      return window.location.origin.replace(/\/+$/, '')
    }
  } catch {
    /* ignore */
  }
  return ''
}

/** 由后端本地磁盘或 /uploads 映射提供的站内资源路径 */
export function isAppHostedPath(pathname: string): boolean {
  const p = pathname.trim()
  return p.startsWith('/uploads/')
}

/** Vite `public/` 下、不经后端 uploads 的静态资源前缀 */
const FRONTEND_PUBLIC_PATH_PREFIXES = ['/login-bg/'] as const

/**
 * 前端静态资源（如登录背景）：不应拼 {@link readAppOrigin}（:8082），
 * 由页面源（Vite :5173 或线上静态站）直接提供。
 */
export function isFrontendPublicAssetPath(pathname: string): boolean {
  const p = pathname.trim()
  return FRONTEND_PUBLIC_PATH_PREFIXES.some((prefix) => p.startsWith(prefix))
}

function isLocalBucket(bucket: string | undefined): boolean {
  const b = bucket?.trim().toLowerCase()
  return !b || b === LOCAL_BUCKET
}

export function looksLikeBareRemoteHost(raw: string): boolean {
  const s = raw.trim()
  if (!s || s.startsWith('/') || /^https?:\/\//i.test(s) || s.startsWith('//')) {
    return false
  }
  return /^[a-z0-9][-a-z0-9.]*\.[a-z]{2,}([/:][^\s]*)?$/i.test(s)
}

function withPageScheme(protocolRelative: string): string {
  const currentScheme =
    typeof window !== 'undefined' && window.location.protocol === 'http:' ? 'http:' : 'https:'
  return `${currentScheme}${protocolRelative}`
}

function parseHttpUrl(raw: string): URL | null {
  const s = raw.trim()
  if (!s) return null
  let candidate = s
  if (candidate.startsWith('//')) {
    candidate = withPageScheme(candidate)
  }
  if (!/^https?:\/\//i.test(candidate)) {
    return null
  }
  try {
    return new URL(candidate)
  } catch {
    return null
  }
}

function isSameOriginAsApp(urlOrigin: string): boolean {
  const app = readAppOriginSafe()
  if (!app) return false
  try {
    return new URL(app).origin === urlOrigin
  } catch {
    return false
  }
}

function readUploadOriginSafe(): string {
  const fromEnv = import.meta.env.VITE_UPLOAD_PUBLIC_ORIGIN
  if (typeof fromEnv === 'string' && fromEnv.trim()) {
    return fromEnv.trim().replace(/\/+$/, '')
  }
  return readAppOriginSafe()
}

/**
 * 开发态：站内 /uploads 的绝对 URL → 同源相对路径，
 * 由 Vite 反代到 {@link readAppOrigin}（默认 :8082），避免误用 :5173 或跨域。
 */
function coerceAppHostedAbsoluteToRelativeInDev(url: string): string | null {
  if (!import.meta.env.DEV) return null
  const trimmed = url.trim()
  if (isAppHostedPath(trimmed) || trimmed.startsWith('/uploads')) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  }
  try {
    const candidate = trimmed.startsWith('//') ? withPageScheme(trimmed) : trimmed
    const parsed = parseHttpUrl(candidate)
    if (parsed && isAppHostedPath(parsed.pathname)) {
      return appHostedRelativePathFromParsedUrl(parsed)
    }
  } catch {
    /* ignore */
  }
  return null
}

/** 开发态浏览器展示：站内资源尽量走 Vite 同源 /uploads 反代 */
export function coerceUploadUrlForDevBrowser(url: string): string {
  const trimmed = url.trim()
  const relativeInDev = coerceAppHostedAbsoluteToRelativeInDev(trimmed)
  if (relativeInDev != null) {
    return relativeInDev
  }
  return trimmed
}

/** 从绝对 URL 提取站内托管路径（/uploads），供拼 API 源 */
function appHostedRelativePathFromParsedUrl(parsed: URL): string {
  return `${parsed.pathname}${parsed.search}${parsed.hash}`
}

/**
 * 区分参考图展示链路：应用内托管（/uploads、本站 API 根） vs 远程 CDN/外链。
 * 路径落在 /uploads 命名空间的一律视为站内（落库 URL 可能带 Vite/API/127.0.0.1 等不同主机）。
 */
export function classifyReferenceImageLink(
  raw: string,
  hints?: ReferenceImageUrlHints,
): ReferenceImageLinkKind {
  const s = raw.trim()
  if (!s) return 'app-hosted'

  if (isAppHostedPath(s) || s.startsWith('/uploads')) {
    return 'app-hosted'
  }

  const parsed = parseHttpUrl(s)
  if (parsed) {
    if (isAppHostedPath(parsed.pathname)) {
      return 'app-hosted'
    }

    if (!isLocalBucket(hints?.bucket)) {
      return 'remote-cdn'
    }
    if (isQiniuCdnHost(parsed.hostname) || isLikelyQiniuHost(parsed.hostname)) {
      return 'remote-cdn'
    }
    if (isSameOriginAsApp(parsed.origin)) {
      return 'app-hosted'
    }
    return 'remote-cdn'
  }

  if (isFrontendPublicAssetPath(s)) {
    return 'app-hosted'
  }

  if (!isLocalBucket(hints?.bucket)) {
    return 'remote-cdn'
  }

  if (looksLikeBareRemoteHost(s)) {
    return 'remote-cdn'
  }

  return 'app-hosted'
}

/**
 * 将后端返回的外链规范为浏览器可稳定请求的地址：保留原协议、路径段编码（避免 objectKey 含非 ASCII 等导致请求失败）。
 */
export function normalizeReferenceImageDisplayUrl(raw: string): string {
  const s = raw.trim()
  if (!s) return s
  let candidate = s
  if (candidate.startsWith('//')) {
    candidate = withPageScheme(candidate)
  }
  try {
    const parsed = new URL(candidate)
    if (isLikelySignedPrivateUrl(parsed)) {
      return parsed.toString()
    }
    const segments = parsed.pathname.split('/').filter(Boolean)
    const encoded = segments.map((seg) => encodeURIComponent(safeDecodePathSegment(seg))).join('/')
    parsed.pathname = encoded ? `/${encoded}` : '/'
    return parsed.toString()
  } catch {
    return candidate
  }
}

function ensureAbsoluteRemoteUrl(raw: string): string {
  const s = raw.trim()
  if (!s) return s
  if (s.startsWith('//')) {
    return withPageScheme(s)
  }
  if (/^https?:\/\//i.test(s)) {
    return s
  }
  return s
}

/**
 * 图库 from-remote / 服务端 image-bytes：保留库内 http(s) 协议，禁止 http→https 升级；
 * 站内资源返回 /uploads 相对路径；无协议的 `//` 补全为 https:（非升级已有 http 链）。
 */
export function resolveStoredImageRemoteUrl(raw: string): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed

  if (isAppHostedPath(trimmed) || trimmed.startsWith('/uploads')) {
    return trimmed.startsWith('/') ? trimmed : `/${trimmed}`
  }

  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed)
      if (isAppHostedPath(parsed.pathname)) {
        return appHostedRelativePathFromParsedUrl(parsed)
      }
    } catch {
      /* keep raw */
    }
    return trimmed
  }

  if (trimmed.startsWith('//')) {
    return `https:${trimmed}`
  }

  if (looksLikeBareRemoteHost(trimmed)) {
    return `https://${trimmed}`
  }

  return trimmed
}

/**
 * 开发时若落库/时间线误带 Vite 源（:5173）而路径实为 /uploads，改拼到 API 源（:8082）。
 */
function rewriteMisplacedPageOriginToAppOrigin(url: string): string {
  const parsed = parseHttpUrl(url)
  if (!parsed || !isAppHostedPath(parsed.pathname)) {
    return url
  }
  const pageOrigin = readPageOriginSafe()
  const appOrigin = readAppOriginSafe()
  if (!pageOrigin || !appOrigin) {
    return url
  }
  try {
    const app = new URL(appOrigin).origin
    if (parsed.origin === pageOrigin && parsed.origin !== app) {
      return `${app}${parsed.pathname}${parsed.search}${parsed.hash}`
    }
  } catch {
    /* ignore */
  }
  return url
}

function joinAppOrigin(path: string): string {
  const origin = readUploadOriginSafe()
  const normalizedPath = path.startsWith('/') ? path : `/${path}`
  return origin ? `${origin}${normalizedPath}` : normalizedPath
}

/** 保留展示链路入口；结构化存储下不再将 CDN URL 改写为本地磁盘路径。 */
export function convertQiniuReferenceUrlToLocal(raw: string): string {
  return raw.trim()
}

/**
 * 按链路解析为浏览器可请求的展示地址。
 * - app-hosted：相对路径或 /uploads → 拼 {@link readAppOrigin}（开发分离时指向 :8082）
 * - remote-cdn：七牛/外链绝对地址，不拼 Vite/API 源
 */
export function resolveReferenceImageDisplayUrl(
  raw: string,
  hints?: ReferenceImageUrlHints,
): string {
  const trimmed = raw.trim()
  if (!trimmed) return trimmed

  if (isFrontendPublicAssetPath(trimmed)) {
    return trimmed
  }

  let working = convertQiniuReferenceUrlToLocal(trimmed)
  const kind = classifyReferenceImageLink(working, hints)

  if (kind === 'remote-cdn') {
    const normalized = normalizeReferenceImageDisplayUrl(ensureAbsoluteRemoteUrl(working))
    return coerceUploadUrlForDevBrowser(normalized)
  }

  working = normalizeReferenceImageDisplayUrl(working)
  working = rewriteMisplacedPageOriginToAppOrigin(working)

  if (/^https?:\/\//i.test(working) || working.startsWith('//')) {
    const candidate = working.startsWith('//') ? withPageScheme(working) : working
    const parsed = parseHttpUrl(candidate)
    if (parsed && isAppHostedPath(parsed.pathname)) {
      const relative = appHostedRelativePathFromParsedUrl(parsed)
      const coerced = coerceUploadUrlForDevBrowser(relative)
      if (import.meta.env.DEV && coerced.startsWith('/') && isAppHostedPath(coerced)) {
        return coerced
      }
      return joinAppOrigin(relative)
    }
    return candidate
  }

  return coerceUploadUrlForDevBrowser(joinAppOrigin(working))
}

/**
 * 若为七牛常见域名且未带 imageView，则追加缩略参数，减轻大图在列表中的加载压力。
 * 仅用于 remote-cdn；应用内 /uploads 不追加 imageView。
 */
export function referenceImageThumbnailUrl(
  normalizedUrl: string,
  width = 480,
  hints?: ReferenceImageUrlHints,
): string {
  if (!normalizedUrl) return normalizedUrl
  if (classifyReferenceImageLink(normalizedUrl, hints) === 'app-hosted') {
    return normalizedUrl
  }
  try {
    const u = new URL(normalizedUrl)
    if (isLikelySignedPrivateUrl(u)) {
      return normalizedUrl
    }
    if (!isLikelyQiniuHost(u.hostname)) {
      return normalizedUrl
    }
    const hay = `${u.pathname}${u.search}`
    if (/imageview2/i.test(hay)) {
      return normalizedUrl
    }
    const param = `imageView2/2/w/${Math.max(64, Math.min(width, 2048))}`
    return u.search ? `${normalizedUrl}&${param}` : `${normalizedUrl}?${param}`
  } catch {
    return normalizedUrl
  }
}

